import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, PENDING_KEY, startProject } from "@/lib/api";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen relative grid lg:grid-cols-2 bg-[#0A0D14]">
      <div className="hidden lg:flex relative flex-col justify-between p-12 border-r border-white/5 overflow-hidden noise">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute inset-0 glow-top" />
        <div className="relative"><Logo /></div>
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-cyan-400">SYNAPSIS</p>
          <h2 className="mt-4 text-4xl font-bold text-white leading-tight max-w-md">Tus ideas, convertidas en software real.</h2>
          <p className="mt-4 text-slate-400 max-w-md">Describe, previsualiza, ajusta y publica aplicaciones web sin escribir una línea de código.</p>
        </div>
        <p className="relative text-xs text-slate-600 font-mono">agente.constructor → activo</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm rise">
          <div className="lg:hidden mb-10"><Logo /></div>
          <h1 className="text-3xl font-bold text-white">{title}</h1>
          <p className="mt-2 text-sm text-slate-400">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

function useAfterAuth() {
  const navigate = useNavigate();
  return async () => {
    const pending = sessionStorage.getItem(PENDING_KEY);
    if (!pending) return navigate("/app");
    sessionStorage.removeItem(PENDING_KEY);
    let job = { text: pending, type: "app" };
    try { job = JSON.parse(pending); } catch { /* plain text from older version */ }
    try {
      const p = await startProject(job.text, undefined, job.type);
      navigate(`/app/proyecto/${p.id}`, { state: { initialPrompt: job.text } });
    } catch {
      navigate("/app");
    }
  };
}

function AuthForm({ fields, submitLabel, endpoint, testPrefix }) {
  const { setUser } = useAuth();
  const after = useAfterAuth();
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, form);
      setUser(data);
      await after();
    } catch (err) {
      setError(formatError(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={`${testPrefix}-form`}>
      {fields.map((f) => (
        <div key={f.name} className="space-y-2">
          <Label htmlFor={f.name} className="text-slate-300">{f.label}</Label>
          <Input id={f.name} type={f.type} required minLength={f.min} value={form[f.name] || ""}
            onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
            data-testid={`${testPrefix}-${f.name}-input`} className="h-11 bg-[#111622]" placeholder={f.placeholder} />
        </div>
      ))}
      {error && <p className="text-sm text-red-400" data-testid={`${testPrefix}-error`}>{error}</p>}
      <button type="submit" disabled={busy} data-testid={`${testPrefix}-submit-btn`}
        className="w-full h-11 rounded-xl bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {submitLabel}
      </button>
    </form>
  );
}

export function Login() {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return (
    <Shell title="Bienvenido de vuelta" subtitle="Inicia sesión para continuar con tus proyectos.">
      <AuthForm endpoint="/auth/login" testPrefix="login" submitLabel="Iniciar sesión" fields={[
        { name: "email", label: "Correo electrónico", type: "email", placeholder: "tu@correo.com" },
        { name: "password", label: "Contraseña", type: "password" },
      ]} />
      <p className="mt-6 text-sm text-slate-400">¿No tienes cuenta? <Link to="/registro" className="text-cyan-400 hover:underline" data-testid="go-register-link">Regístrate</Link></p>
    </Shell>
  );
}

export function Register() {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return (
    <Shell title="Crea tu cuenta" subtitle="Tu espacio de trabajo privado para construir aplicaciones.">
      <AuthForm endpoint="/auth/register" testPrefix="register" submitLabel="Crear cuenta" fields={[
        { name: "name", label: "Nombre", type: "text", placeholder: "Tu nombre" },
        { name: "email", label: "Correo electrónico", type: "email", placeholder: "tu@correo.com" },
        { name: "password", label: "Contraseña (mínimo 8 caracteres)", type: "password", min: 8 },
      ]} />
      <p className="mt-6 text-sm text-slate-400">¿Ya tienes cuenta? <Link to="/login" className="text-cyan-400 hover:underline" data-testid="go-login-link">Inicia sesión</Link></p>
    </Shell>
  );
}
